import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ColorModeProvider, useColorMode } from './contexts/ColorModeContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { SearchSettingsProvider } from './contexts/SearchSettingsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import MainLayout from './components/layout/MainLayout';
import ExpirationWarning from './components/auth/ExpirationWarning';
import AnnouncementBanners from './components/auth/AnnouncementBanners';
import AdminConsolePage from './pages/AdminConsolePage';
import { createAppTheme } from './theme/appTheme';

const ThemedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { resolvedMode } = useColorMode();
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-narralizer-theme', resolvedMode);
  }, [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Inline TokenHandler component
const TokenHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setToken } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    console.log('TokenHandler running. Token:', token);
    if (token) {
      setToken(token);
      navigate('/', { replace: true });
    }
  }, [location, setToken, navigate]);

  return null;
};

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const [warningOpen, setWarningOpen] = useState(true);

  const showWarning = Boolean(
    user &&
      !user.accountNeverExpires &&
      user.expirationDate &&
      warningOpen &&
      (() => {
        const expirationDate = new Date(user.expirationDate);
        const now = new Date();
        const daysUntilExpiration = Math.ceil(
          (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
        );
        return daysUntilExpiration <= 7;
      })(),
  );

  useEffect(() => {
    setWarningOpen(true);
  }, [user?.id]);

  return (
    <>
      {user && <AnnouncementBanners />}
      {showWarning && user && (
        <ExpirationWarning user={user} open={warningOpen} onClose={() => setWarningOpen(false)} />
      )}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout showWarning={showWarning} />
            </ProtectedRoute>
          }
        >
          <Route path="admin" element={<AdminConsolePage />} />
        </Route>
      </Routes>
    </>
  );
};

const App: React.FC = () => (
  <ColorModeProvider>
    <ThemedShell>
      <Router>
        <AuthProvider>
          <FeatureFlagsProvider>
          <Routes>
            <Route path="/oauth2/callback/*" element={<TokenHandler />} />
            <Route
              path="/*"
              element={
                <DocumentProvider>
                  <SearchSettingsProvider>
                    <AppContent />
                  </SearchSettingsProvider>
                </DocumentProvider>
              }
            />
          </Routes>
          </FeatureFlagsProvider>
        </AuthProvider>
      </Router>
    </ThemedShell>
  </ColorModeProvider>
);

export default App;
